import React, {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
  MouseEvent,
  CSSProperties,
  useRef,
} from 'react';
import { Presentation } from './presentation';
import type { Props, Occupation, OccupationDetail } from './types';

import { scrollToElementById } from '../../../libs/scrollUtils';
import { sortManagerOccupationDetailsFirst } from '../../../libs/occupationValidation';

export const Component: FunctionComponent<Props> = ({
  shouldTriggerSearch,
  isFromRecruitmentPage,
  target_employment_types,
  target_occupations,
  target_occupation_details,
  target_prefectures,
  target_prefecture_details,
  q: initialQ,
  page,
  recruitment_employment_types,
  occupations,
  side_prefectures,
  all_prefectures,
  detail_prefectures,
  occupation_details,
  search_occupation_details,
  occupationDetailsOccupationIds,
  prefectureDetails,
  isPc,
  isSp,
  isPage,
  popupStyle,
  modalStyle,
  spStyle,
  toggleListStyle,
  recruitmentsCount,
  handleClick,
  handleSearch: onSearch,
  handleChangeUrl,
  handleChangeQ: onChangeQ,
  checkAllOccupationDetails,
  checkAllPrefectureDetails,
  handleFetchOccupationDetails,
  handleFetchPrefectureDetails,
  openList,
  handlePopup,
  handleModal,
  handleOpenSp,
  closePopup,
  handleClearAll,
  handleClearEmploymentTypes,
  handleSelectAllEmploymentTypes,
  handleClearOccupations,
  handleSelectAllOccupations,
  handleClearLocations,
  handleSelectAllLocations,
  target_preference_conditions,
  target_working_periods,
  preference_condition_categories,
  working_period_ids,
  salary_type_ids,
  handleClearPreferenceConditions,
  handleSelectAllPreferenceConditions,
  target_salary_type,
  target_salary_min,
  target_salary_max,
  handleChangeSalary,
  handleClearAreas,
  handleClearPrefectureDetails,
  searchHistories,
  handleApplySearchHistory,
  handleSaveConditions,
  saveConditions,
  handleDeleteSaveConditions,
}) => {
  const [bigListStyle, setBigListStyle] = useState<CSSProperties>({ width: '78%' });
  const [smallListStyle, setSmallListStyle] = useState<CSSProperties>({ display: 'none', width: '39%' });
  const [fetchList, setFetchList] = useState('雇用形態');
  const [fetchId, setFetchId] = useState('');
  const [q, setQ] = useState(initialQ || '');

  const isMountedSearch = useRef(false);
  const isMountedChangeUrl = useRef(false);

  useEffect(() => {
    setQ(initialQ || '');
  }, [initialQ]);

  useEffect(() => {
    if (isMountedSearch.current) {
      if (isFromRecruitmentPage) {
        if (shouldTriggerSearch) {
          handleSearch();
        }
      } else {
        handleSearch();
      }
    } else {
      isMountedSearch.current = true;
    }
  }, [
    target_employment_types,
    target_occupation_details,
    target_occupations,
    target_prefectures,
    target_prefecture_details,
    target_preference_conditions,
    target_working_periods,
    target_salary_type,
    target_salary_min,
    target_salary_max,
  ]);

  useEffect(() => {
    if (isMountedChangeUrl.current && isPage !== 'recruitments' && isPage !== 'static') {
      if (handleChangeUrl) {
        handleChangeUrl('');
      }
    } else {
      isMountedChangeUrl.current = true;
    }
  }, [page, isPage, handleChangeUrl]);

  const handleSearch = useCallback(() => {
    if (isPage !== 'recruitments') {
      if (handleChangeUrl) {
        handleChangeUrl('');
      }
    }
    onSearch('');
  }, [isPage, handleChangeUrl, onSearch]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.name === 'target_occupations') {
        const { value, checked } = event.target;

        checkAllOccupationDetails(value, checked);
        handleClick(event);
      } else if (event.target.name === 'target_prefectures') {
        const checked = event.currentTarget.checked;
        const value = event.target.value;
        let baseArray: string[] = [];

        if (value === '北海道・東北') {
          baseArray = baseArray.concat(['1', '2', '3', '4', '5', '6', '7']);
        } else if (value === '関東') {
          baseArray = baseArray.concat(['8', '9', '10', '11', '12', '13', '14']);
        } else if (value === '北陸・甲信越') {
          baseArray = baseArray.concat(['15', '16', '17', '18', '19', '20']);
        } else if (value === '東海') {
          baseArray = baseArray.concat(['21', '22', '23', '24']);
        } else if (value === '近畿') {
          baseArray = baseArray.concat(['25', '26', '27', '28', '29', '30']);
        } else if (value === '中国・四国') {
          baseArray = baseArray.concat(['31', '32', '33', '34', '35', '36', '37', '38', '39']);
        } else if (value === '九州・沖縄') {
          baseArray = baseArray.concat(['40', '41', '42', '43', '44', '45', '46', '47']);
        }

        checkAllPrefectureDetails(baseArray, value, checked);
        handleClick(event);
      } else {
        handleClick(event);
      }
    },
    [checkAllOccupationDetails, checkAllPrefectureDetails, handleClick]
  );

  const fetchIds = useCallback((event: MouseEvent<HTMLLIElement>) => {
    const input = event.currentTarget.querySelector('input');
    if (input) {
      const { value, name } = input;
      if (name !== 'target_occupation_details' && name !== 'target_prefecture_details') {
        setFetchId(value);
      }
    }
    return false;
  }, []);

  const handleChangeQ = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;
      setQ(value);
      onChangeQ('q', value);
    },
    [onChangeQ]
  );

  const handleBlurQ = useCallback(() => {
    onSearch('');
  }, [onSearch]);

  const handleChangeList = useCallback((event: MouseEvent<HTMLLIElement>) => {
    const text = event.currentTarget.querySelector('span')?.innerHTML || '';
    setFetchList(text);
    setFetchId('');
    if (text === '職種' || text === '勤務地') {
      setSmallListStyle({ display: 'block', width: '39%' });
      setBigListStyle({ width: '39%' });
    } else {
      setSmallListStyle({ display: 'none' });
      setBigListStyle({ width: '78%' });
    }
  }, []);

  const smoothScrollToArticles = useCallback(() => {
    const articlesElement = document.querySelector('.l-articles');
    if (articlesElement) {
      const height = (articlesElement as HTMLElement).offsetTop;
      window.scrollTo({ top: height, behavior: 'smooth' });
    }
  }, []);

  const handleClickBtn = useCallback(() => {
    smoothScrollToArticles();
    handleOpenSp();
    onSearch('');
  }, [smoothScrollToArticles, handleOpenSp, onSearch]);

  const handleClickSubmit = useCallback(() => {
    smoothScrollToArticles();
    closePopup();
    onSearch('');
  }, [smoothScrollToArticles, closePopup, onSearch]);

  const checkPrefectureArea = useCallback(
    (key: string): boolean => {
      const details = prefectureDetails?.[key];
      if (!details) return false;
      const lengthMap: { [key: string]: number } = {
        '北海道・東北': 7,
        関東: 7,
        '北陸・甲信越': 6,
        東海: 4,
        近畿: 6,
        '中国・四国': 9,
        '九州・沖縄': 8,
      };
      return details.length === lengthMap[key];
    },
    [prefectureDetails]
  );

  const checkPrefectureAreaSp = useCallback(
    (key: string): boolean => {
      return (prefectureDetails?.[key]?.length || 0) > 0;
    },
    [prefectureDetails]
  );

  const handleScrollTo = useCallback((name: string) => {
    scrollToElementById(`occupation_${name}`);
  }, []);

  const fetchDetails = useCallback(
    (o: Occupation): OccupationDetail[] => {
      return sortManagerOccupationDetailsFirst(
        occupation_details?.[o.id - 1]?.occupation_details || []
      );
    },
    [occupation_details]
  );

  const presentationProps = {
    target_employment_types,
    target_occupations,
    target_occupation_details,
    target_prefectures,
    target_prefecture_details,
    recruitment_employment_types,
    occupations,
    side_prefectures,
    all_prefectures,
    detail_prefectures,
    occupation_details,
    search_occupation_details,
    occupationDetailsOccupationIds,
    prefectureDetails,
    isPc,
    isSp,
    isPage,
    popupStyle,
    modalStyle,
    spStyle,
    toggleListStyle,
    recruitmentsCount,
    handleClick,
    handleChangeUrl,
    handleFetchOccupationDetails,
    handleFetchPrefectureDetails,
    openList,
    handlePopup,
    handleModal,
    handleOpenSp,
    closePopup,
    handleClearAll,
    handleClearEmploymentTypes,
    handleSelectAllEmploymentTypes,
    handleClearOccupations,
    handleSelectAllOccupations,
    handleClearLocations,
    handleSelectAllLocations,
    target_preference_conditions,
    target_working_periods,
    preference_condition_categories,
    working_period_ids,
    salary_type_ids,
    handleClearPreferenceConditions,
    handleSelectAllPreferenceConditions,
    target_salary_type,
    target_salary_min,
    target_salary_max,
    handleChangeSalary,
    handleClearAreas,
    handleClearPrefectureDetails,
    searchHistories,
    handleApplySearchHistory,
    handleSaveConditions,
    saveConditions,
    handleDeleteSaveConditions,
    q,
    fetchList,
    fetchId,
    fetchIds,
    smallListStyle,
    bigListStyle,
    handleSearch,
    handleChange,
    handleChangeQ,
    handleBlurQ,
    handleClickBtn,
    handleChangeList,
    fetchDetails,
    checkPrefectureArea,
    checkPrefectureAreaSp,
    handleClickSubmit,
    handleScrollTo,
  } as any;

  return <Presentation {...presentationProps} />;
};
